// frontend/src/components/photos/PhotosPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import PhotoUploader from "./PhotoUploader";
import PhotosGrid from "./PhotosGrid";
import VideoPreviewGallery from "./VideoPreviewGallery.jsx";
import CuradoriaPage from "./CuradoriaPage.jsx";

const API_BASE = import.meta.env.VITE_BACKEND_URL || "https://api.viadrive.com";

export default function PhotosPage({ albumId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllTok, setSelectAllTok] = useState(0);
  const [clearTok, setClearTok] = useState(0);
  const [showCuradoria, setShowCuradoria] = useState(false);

  async function load() {
    if (!albumId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(
        `${API_BASE}/api/photos?album=${encodeURIComponent(albumId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) throw new Error(`Falha ao listar fotos (${r.status})`);
      setItems(await r.json());
    } catch (e) {
      console.error(e);
      toast.error("Não foi possível carregar o álbum.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [albumId]);

  // Helpers
  const isVideoItem = (it) => {
    const name = (it.name || it.key || it.objectKey || it.filename || "").toLowerCase();
    return /\.(mp4|webm|mov)$/.test(name);
  };

  function filenameFromItem(it) {
    return it.name || it.key || it.objectKey || it.filename || "";
  }

  function dirFromUrl(u) {
    try {
      const url = new URL(u);
      const parts = url.pathname.split("/");
      parts.pop(); // remove filename
      url.pathname = parts.join("/") + "/";
      return url.toString().replace(/\/+$/, ""); // sem / final
    } catch {
      return "";
    }
  }

  function buildSiblingUrl(it, siblingFilename) {
    // 1) Se veio URL pública do arquivo principal, usa a mesma pasta
    if (it.url) {
      const baseDir = dirFromUrl(it.url);
      if (baseDir) return `${baseDir}/${encodeURIComponent(siblingFilename)}`;
    }
    // 2) Se veio algum cdnBase/baseUrl custom no seu objeto
    if (it.baseUrl) {
      return `${it.baseUrl.replace(/\/+$/, "")}/${encodeURIComponent(siblingFilename)}`;
    }
    // 3) Fallback (ajuste se tiver endpoint específico pra arquivo):
    //    Ex.: /api/photos/file?album=...&key=...
    return `${API_BASE}/api/photos/file?album=${encodeURIComponent(albumId)}&key=${encodeURIComponent(siblingFilename)}`;
  }

  const videoItems = useMemo(() => {
    const vids = items.filter(isVideoItem);
    return vids.map((it) => {
      const name = filenameFromItem(it);
      const base = name.replace(/\.(mp4|webm|mov)$/i, "");
      const posterName  = `${base}_poster.jpg`;
      const previewName = `${base}_preview.mp4`;

      // URL do arquivo principal:
      const src =
        it.url ||
        buildSiblingUrl(it, `${base}.mp4`); // caso tenha sido .webm/mov, ajuste aqui se preferir

      return {
        id: it.id || base,
        title: (base.split("/").pop() || base),
        poster: it.posterUrl || buildSiblingUrl(it, posterName),
        preview: it.previewUrl || buildSiblingUrl(it, previewName),
        src,
        href: src,
        duration: it.duration || "", // opcional
      };
    });
  }, [items, albumId]);

  const isVideoAlbum = useMemo(() => videoItems.length > 0 && videoItems.length === items.filter(isVideoItem).length, [videoItems, items]);

  // Ações (mantidas para fotos)
  async function apiAuth(path, method, body) {
    const token = localStorage.getItem("token");
    const r = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { "Content-Type":"application/json", Authorization: `Bearer ${token}` },
      body: body ? JSON.stringify(body) : undefined
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r;
  }

  async function onDelete(ids) {
    if (!ids?.length) return;
    if (!confirm(`Excluir ${ids.length} item(ns)?`)) return;
    await apiAuth("/api/photos/bulk-delete","POST",{ ids });
    setItems(prev => prev.filter(p => !ids.includes(p.id)));
    setSelectedIds([]);
    setClearTok((t)=>t+1);
  }

  async function onZip(ids, variant="full-wm") {
    if (!ids?.length) return;
    const r = await apiAuth("/api/photos/zip","POST",{ ids, variant });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `album-${Date.now()}.zip`; a.click();
    setTimeout(()=>URL.revokeObjectURL(url), 5000);
  }

  async function onMove(ids) {
    if (!ids?.length) return;
    const toAlbum = prompt("Mover para álbum (id):");
    if (!toAlbum) return;
    await apiAuth("/api/photos/move","POST",{ ids, toAlbum: toAlbum.trim() });
    await load();
    setSelectedIds([]);
    setClearTok((t)=>t+1);
  }



  
  // Atalhos: A, Esc, Del, Z (apenas para fotos)
  useEffect(() => {
    if (isVideoAlbum) return; // atalhos não fazem sentido na galeria de vídeo
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key.toLowerCase() === "a") { setSelectAllTok(t=>t+1); e.preventDefault(); }
      if (e.key === "Escape") { setClearTok(t=>t+1); }
      if (e.key === "Delete") { onDelete(selectedIds); }
      if (e.key.toLowerCase() === "z") { onZip(selectedIds, "full-wm"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedIds, isVideoAlbum]);

  return (
    <div className="container" style={{maxWidth: 1200, margin: "0 auto", padding: 16}}>
      {/* Toolbar */}
      <div style={{
        position:'sticky', top:0, background:'#fff', zIndex:5, padding:'8px 0',
        display:'flex', gap:8, alignItems:'center', borderBottom:'1px solid #eee'
      }}>
        <h2 style={{margin:0, fontSize:18}}>
          Álbum {albumId || "-"} {isVideoAlbum ? "• Vídeos" : ""}
        </h2>
        <div style={{flex:1}} />
        {!isVideoAlbum && (
          <>
            <button onClick={() => setSelectAllTok(t=>t+1)}>Selecionar tudo</button>
            <button onClick={() => setClearTok(t=>t+1)}>Limpar</button>
            <button onClick={() => onDelete(selectedIds)} disabled={!selectedIds.length}>Excluir</button>
            <button onClick={() => onZip(selectedIds, "full-wm")} disabled={!selectedIds.length}>Baixar ZIP</button>
            <button onClick={() => onMove(selectedIds)} disabled={!selectedIds.length}>Mover</button>
            <button onClick={()=>setShowCuradoria(s=>!s)}>{showCuradoria ? 'Ver fotos' : 'Ver curadoria'}</button>
          </>
        )}
      </div>

      {/* Uploader (mantém; se for vídeo, você pode trocar por um uploader de vídeo depois) */}
      <PhotoUploader albumId={albumId} onDone={load} />

     {loading ? (
  <p>Carregando...</p>
) : showCuradoria ? (
  <CuradoriaPage albumId={albumId} />
) : items.length ? (
  isVideoAlbum ? (
    <VideoPreviewGallery items={videoItems} title="Vídeos" />
  ) : (
    <PhotosGrid
      items={items}
      onSelectionChange={setSelectedIds}
      selectAllToken={selectAllTok}
      clearSelectionToken={clearTok}
    />
  )
      ) : (
        <p>Nenhum item ainda.</p>
      )}
    </div>

    
  );
}
